import { useState } from 'react';
import { getActiveLocations } from '@/api/admin/location';
import {
	add,
	ImportTypeLocationState,
	ImportTypeLocationStateSchema,
} from '@/api/admin/location-state';
import { Button } from '@/components/ui/button';
import { ComboboxSelect } from '@/components/ui/custom/combobox-select';
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectLabel,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
} from '@/components/ui/sheet';
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '@/components/ui/tooltip';
import { states } from '@/constants/states';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarIcon, CirclePlus } from 'lucide-react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';

export function AddImportTypeLocationState({
	importTypeId,
}: {
	importTypeId: number;
}) {
	const { data: activeLocations, isLoading: isLoadingActiveLocations } =
		useQuery({
			queryKey: [`active-locations`],
			queryFn: () => getActiveLocations(),
			staleTime: 5 * 60 * 1000, // Cache for 5 minutes
		});

	const [open, setOpen] = useState(false);
	const [isOtherSelected, setIsOtherSelected] = useState(false);
	const queryClient = useQueryClient();

	const { mutate } = useMutation({
		mutationFn: (newImportTypeLocationState: ImportTypeLocationState) =>
			add(newImportTypeLocationState),
		onSuccess: () => {
			toast.success(`Success! Location State has been successfully added.`, {
				dismissible: true,
			});
			setOpen(false);
			queryClient.invalidateQueries({
				queryKey: [`locationStates-${importTypeId}`],
			});
			reset();
			setIsOtherSelected(false);
		},
		onError: () => {
			toast.error(`Error! Location State could not be added.`, {
				dismissible: true,
			});
		},
	});

	const form = useForm<ImportTypeLocationState>({
		resolver: zodResolver(ImportTypeLocationStateSchema),
		mode: 'all',
		defaultValues: {
			importTypeId: importTypeId,
			expired: false,
			beginDate: new Date(),
		},
	});

	const { reset } = form;
	const onSubmit: SubmitHandler<ImportTypeLocationState> = (
		values: ImportTypeLocationState,
	) => mutate(values);

	return (
		<div>
			<TooltipProvider>
				<Tooltip>
					<TooltipTrigger asChild>
						<Button className="m-auto" onClick={() => setOpen(true)}>
							<CirclePlus />
							Add New Location State
						</Button>
					</TooltipTrigger>
					<TooltipContent>
						<p>Add a new location state</p>
					</TooltipContent>
				</Tooltip>
			</TooltipProvider>

			<Sheet open={open} onOpenChange={(open) => setOpen(open)}>
				<SheetContent>
					<SheetHeader>
						<SheetTitle>Add a New Location State</SheetTitle>
						<SheetDescription></SheetDescription>
					</SheetHeader>
					<Form {...form}>
						<form onSubmit={form.handleSubmit(onSubmit)}>
							<div className="space-y-4">
								<FormField
									control={form.control}
									name="importTypeId"
									render={({ field }) => (
										<FormItem>
											<FormLabel>Import Type ID</FormLabel>
											<FormControl>
												<Input {...field} value={field.value ?? ''} disabled={true} />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={form.control}
									name="compLocId"
									render={({ field }) => (
										<ComboboxSelect
											field={field}
											options={activeLocations}
											valueKey="compLocId"
											displayKey="brandName"
											isLoading={isLoadingActiveLocations}
											label="Company Location"
											placeholder="Select company location..."
											searchPlaceholder="Search company locations..."
											required={true}
											emptyMessage="No company location found."
											loadingMessage="Loading company locations..."
										/>
									)}
								/>
								<FormField
									control={form.control}
									name="stateId"
									render={({ field }) => (
										<FormItem>
											<FormLabel>State</FormLabel>
											<FormControl>
												<Select
													value={field.value ?? 'none'}
													onValueChange={(val) =>
														field.onChange(val === 'none' ? null : val)
													}
												>
													<SelectTrigger>
														<SelectValue placeholder="Select a state..." />
													</SelectTrigger>
													<SelectContent>
														<SelectGroup>
															<SelectLabel>States</SelectLabel>
															{states!.map((state) => (
																<SelectItem key={state} value={state}>
																	{state}
																</SelectItem>
															))}
														</SelectGroup>
													</SelectContent>
												</Select>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={form.control}
									name="beginDate"
									render={({ field }) => (
										<FormItem className="flex flex-col">
											<FormLabel>Begin Date</FormLabel>

											<Popover>
												<PopoverTrigger asChild>
													<FormControl>
														<Button
															variant={'outline'}
															className={`w-full pl-3 text-left font-normal ${
																!field.value && 'text-muted-foreground'
															}`}
														>
															{field.value ? (
																new Date(field.value).toLocaleDateString()
															) : (
																<span>Pick a date</span>
															)}
															<CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
														</Button>
													</FormControl>
												</PopoverTrigger>

												<PopoverContent className="w-auto p-0">
													<Calendar
														mode="single"
														selected={field.value ? new Date(field.value) : undefined}
														onSelect={(date) => field.onChange(date)}
														captionLayout="dropdown"
														endMonth={new Date(new Date().getFullYear() + 2, 11)}
													/>
												</PopoverContent>
											</Popover>

											<FormMessage />
										</FormItem>
									)}
								/>
							</div>

							<div className="mt-9 flex justify-end">
								<Button
									type="submit"
									disabled={!form.formState.isValid || form.formState.isSubmitting}
								>
									{form.formState.isSubmitting ? 'Adding...' : 'Add'}
								</Button>
							</div>
						</form>
					</Form>
				</SheetContent>
			</Sheet>
		</div>
	);
}
